import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { ExtenderSesionService } from "./extender-sesion.service";
import { InactivityService } from "./inactivity.service";
import { SessionService } from "./session.service";

/**
 * Extiende automáticamente la sesión cada `intervaloMs` si el usuario
 * estuvo activo dentro de la ventana de `inactividadMaxMs`.
 *
 * Si lleva más tiempo sin actividad, deja de extender y la sesión
 * se consume hasta expirar naturalmente (lo maneja SessionTimerService).
 */
@Injectable({ providedIn: "root" })
export class AutoExtenderSesionService implements OnDestroy {
    private readonly intervaloMs = 4 * 60 * 1000;
    private readonly inactividadMaxMs = 15 * 60 * 1000;
    private readonly minutosProlongacion = 10;

    private intervalId: ReturnType<typeof setInterval> | null = null;
    private running = false;
    private inFlightSub: Subscription | null = null;

    constructor(
        private extenderSesionService: ExtenderSesionService,
        private inactivityService: InactivityService,
        private sessionService: SessionService,
        private ngZone: NgZone
    ) {}

    start(): void {
        if (this.running) return;
        this.running = true;

        this.ngZone.runOutsideAngular(() => {
            this.intervalId = setInterval(() => this.tick(), this.intervaloMs);
        });
    }

    stop(): void {
        this.running = false;
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.inFlightSub?.unsubscribe();
        this.inFlightSub = null;
    }

    ngOnDestroy(): void {
        this.stop();
    }

    private tick(): void {
        if (!this.sessionService.isAuthenticated()) return;
        if (this.inactivityService.msDesdeUltimaActividad() > this.inactividadMaxMs) {
            return;
        }
        if (this.inFlightSub && !this.inFlightSub.closed) {
            return;
        }

        this.inFlightSub = this.extenderSesionService
            .extenderSesion<any>(this.minutosProlongacion)
            .subscribe({
                next: (response) => {
                    if (!response?.success) {
                        console.warn("[AutoExtenderSesion] Respuesta no exitosa:", response);
                        return;
                    }
                    const sesion = this.sessionService.getSesionActual();
                    if (!sesion) return;

                    const actual = sesion.expiracion
                        ? new Date(sesion.expiracion).getTime()
                        : Date.now();
                    const nuevaExpiracion = new Date(
                        actual + this.minutosProlongacion * 60 * 1000
                    ).toISOString();

                    this.ngZone.run(() => {
                        this.sessionService.actualizarFechasSesion(nuevaExpiracion);
                    });
                },
                error: (err) => {
                    console.error("[AutoExtenderSesion] Error al extender sesión:", err);
                },
            });
    }
}
